import angular from 'angular'
import compact from 'lodash/compact'
import find from 'lodash/find'
import flatten from 'lodash/flatten'
import groupBy from 'lodash/groupBy'
import identity from 'lodash/identity'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import some from 'lodash/some'
import uniqBy from 'lodash/uniqBy'

function lowerCaseTrim(string) {
  if (typeof string !== 'string') {
    return string
  }

  return string.toLowerCase().trim()
}

const NULL_LABEL_GROUP = '__VD_SUGGESTION_SOURCE_NULL_LABEL'

export default function ($q, $log, VdSuggestionFormatter) {
  'ngInject'
  /**
   * VdSuggestionSource is the engine behind Autocomplete, Autocomplete Multi and Dropdown. The VdSuggestionSource API
   * allows you to configure how these components search for, format, display, and create suggestions.
   *
   * @class VdSuggestionSource
   */
  class VdSuggestionSource {
    /**
     * Creates an instance of VdSuggestionSource.
     *
     * @constructor
     *
     * @param {Object} options
     *        @param {Array<Type>} options.types
     *               Array of one or more types indicating the type of results returned by this source. A type
     *               can simply be a string of the type name e.g. 'product' or a configuration object with the
     *               name of the type and additional info on how the type should be displayed in the UI e.g. {
     *               name: 'product_type', display: 'type' }. If supplying a configuration object the name
     *               property is required; other supported properties are display (defaults to `${name}`) and
     *               displayPlural (defaults to `${display}s`).
     *        @param {Array<VdSuggestionSource>} [options.sources]
     *               You can create a composite source by passing an Array of other sources. When the composite source
     *               is searched it will delegate to each of the other sources and collate the results. If specifying
     *               sources the types, init and search options will be ignored and instead composed of the
     *               corresponding options from the given sources.
     *        @param {Array<VdSuggestionFormatter>} [options.formatters]
     *               Array of VdSuggestionFormatters. Each formatter corresponds to a specific type and can be used to
     *               enhance the suggestion with additional data (e.g. price information) and influence how it is
     *               displayed. If no formatter is provided for a specified type one will be created for it with
     *               default configuration.
     *        @param {Function} [options.init]
     *               Optional function to perform initialisation logic e.g. load and precache data from API or
     *               initialise an IndexedDB store. If the init logic is asynchronous return a promise that is resolved
     *               once init is complete.
     *        @param {Function} [options.search]
     *               The function that will do the actual searching. Will be passed an object with a query property
     *               containing the search query the user has typed and should return an Array of Suggestions (or a
     *               promise that resolves to one). If no results are found return an empty Array, if there was an
     *               error return a rejected promise.
     *        @param {Function} [options.feelingLucky]
     *               Optional function hook to support quick selection of the best/first match to a query (e.g. for
     *               barcode scanning a SKU). Will be passed the same arguments as passed to search and should return a
     *               single Suggestion or a promise that resolves to one if successful. If there is no suitable match
     *               to the given query return undefined or a promise that resolves to undefined.
     *        @param {Function} [options.create]
     *               Optional function hook to create a new item (Brand, Supplier, etc) from the given query if there
     *               is no suitable suggestion already available. This is only supported if the source is configured
     *               with a single type. Will be passed an object with a query property containing the search query the
     *               user has typed and should return a Suggestion for the newly created item or a promise that
     *               resolves to one. If the user cancels the creation of the item then return undefined or a promise
     *               that resolves to undefined. If there is an error creating the item return a rejected promise.
     *        @param {Function} [options.canShowCreate]
     *               Optional function helps determine if the add new item footer should be visible.
     *               It should return a value of boolean type. If the function isn't defined, the add new item footer's
     *               visibility will be controlled by the canCreate function. If it is defined, the visibility will
     *               depend on the results of both functions."
     *        @param {Function} [options.select]
     *               Optional function hook to process a selected suggestion. Will be passed an object with a
     *               `suggestion` property containing the suggestion that was selected and should return the suggestion
     *               or a promise that resolves to the suggestion to complete the selection. You can modify the
     *               suggestion or return a completely different one (e.g. present the user with a modal to choose
     *               between all variants of a product or a specific variant (SKU)). You can abort the selection
     *               by returning `undefined` or a promise that rejects or resolves to `undefined`.
     *        @param {Boolean} [options.rawSuggestions]
     *               If the source is configured with only a single type and this is set to true then you can return
     *               raw entity objects of the specified type (e.g. products, suppliers, etc) from search, feelingLucky
     *               or create and the source will convert them to the Suggestion format for you.
     *        @param {String} [options.noSelectionLabel]
     *               Optionally specify the label to use for an option in the suggestion list that represents no
     *               selection/value (only really makes sense for Dropdown) e.g. 'All Outlets'. If supplied then this
     *               suggestion will be displayed in the list (with underlying value of undefined) unless filtered out
     *               by the current query value.
     *        @param {Function} [options.isDuplicate]
     *               Optional function hook to check if the query entered is a duplicate of a retrieved or selected
     *               suggestion. This function is responsible (in conjunction with a provided `create` function) for
     *               determining the visibility of the “Add” item in the suggestion list. It should return a Boolean.
     *        @param {Boolean} [options.allowFeelingLuckyCreate]
     *               Optional, defaults to true. If set to false, the create function will not be called during the
     *               feelingLucky flow.
     */
    constructor({
      types = [],
      sources,
      formatters = [],
      init = identity,
      search,
      feelingLucky,
      create,
      canShowCreate,
      isDuplicate,
      select,
      rawSuggestions,
      noSelectionLabel,
      allowFeelingLuckyCreate = true,
    }) {
      this._select = select
      this._rawSuggestions = rawSuggestions
      this.noSelectionLabel = noSelectionLabel
      this.canShowCreate = canShowCreate
      this.allowFeelingLuckyCreate = allowFeelingLuckyCreate

      if (sources) {
        this._sources = sources
        this.types = uniqBy(
          flatten(map(this._sources, source => source.types)),
          type => this._getTypeName(type)
        )
        this._init = () => $q.all(map(this._sources, source => source.init()))
        this._doSearch = this._searchSources
      } else {
        this.types = types
        this._init = init
        this._doSearch = search
      }

      if (feelingLucky) {
        this._feelingLucky = feelingLucky
        this.feelingLucky = this._doFeelingLucky
      }

      if (create) {
        this._create = create
        this.create = this._doCreate
      }

      if (isDuplicate) {
        this._isDuplicate = isDuplicate
      }

      this._formatters = {}
      formatters.forEach(formatter => {
        this._formatters[this._getTypeName(formatter.type)] = formatter
      })
      this.types.forEach(type => {
        const typeName = this._getTypeName(type)
        if (!this._formatters[typeName]) {
          this._formatters[typeName] = new VdSuggestionFormatter({
            type: typeName,
          })
        }
      })
    }

    // ============================== Public API =============================== //

    /**
     * Initialise the source, calling the given `init` function if configured.
     *
     * @method init
     *
     * @return {Promise} A promise resolved once initialisation is complete
     */
    init() {
      if (!this._whenInitialised) {
        this._whenInitialised = $q.resolve(this._init())
      }
      return this._whenInitialised
    }

    /**
     * Search for the given query.
     *
     * @method search
     *
     * @param  {Object} data
     *         @param {String} data.query The query string to search for
     *
     * @return {Promise<Object>} A promise resolved with the results of the search. The returned objected will have
     *         the following properties:
     *
     *         - query {String} The query that was searched for
     *         - suggestions {Array<Suggestion>} The array of suggestions found in the search
     *         - groups {Array<Group>} The array of suggestion groups if groupBy is configured
     */
    search({ query }) {
      return this._search({ query })
        .then(suggestions => ({ suggestions, query }))
        .then(data => this._insertNullSuggestion(data))
        .then(data => this._groupBy(data))
    }

    /**
     * Perform a feelingLucky search (if configured) on the given query and return the match found. If this source has
     * been configured with create and feelingLucky doesn't return a suggestion (and create is currently possible) then
     * the create pipeline will be triggered.
     *
     * @method feelingLucky
     *
     * @param  {Object} data
     *         @param {String} data.query
     *                The query string to search for
     *         @param {Suggestion} data.pendingSuggestion
     *                The active suggestion in the list that would have been selected if feelingLucky had not been
     *                triggered
     *         @param {Array<Suggestions>} options.suggestions
     *                Array of currently displayed suggestions (to determine create eligibility)
     *         @param {Suggestion|Array<Suggestions>} options.selected
     *                The already selected suggestion(s) (to determine create eligibility)
     *
     * @return {Promise<Suggestion>|undefined} A promise for the Suggestion that matches the given query or undefined
     *         (or promise therefor) if feelingLucky is not configured or there was no match found.
     */
    // Method optionally assigned in constructor if configured

    /**
     * Create a new item (Brand, Supplier, etc) from the given query and return the Suggestion for the newly created
     * item.
     *
     * @method create
     *
     * @param  {Object} data
     *         @param {String} data.query The query string to search for
     *
     * @return {Promise<Suggestion>} A promise for the created Suggestion or that rejects or resolves to undefined if
     *         creating the Suggestion was cancelled/errored
     */
    // Method optionally assigned in constructor if configured

    /**
     * Process the given suggestion for selection, invoking the `select` hook if configured.
     *
     * @method select
     *
     * @param {Object} options
     *        @param {Suggesiton} options.suggestion
     *
     * @return {Suggestion|Promise<Suggestion>|undefined} The suggestion to select (or promise therefor) or undefined or
     *         a promise that rejects or resolves to undefined if the selection is to be cancelled.
     */
    select({ suggestion }) {
      if (this._select) {
        try {
          return this._select({ suggestion })
        } catch (error) {
          $log.warn('[VdSuggestionSource] Error selecting suggestion', error)
          // Return undefined to cancel selection
          return
        }
      }

      return suggestion
    }

    /**
     * Get the text to display to the user for the given suggestion.
     *
     * @method getDisplayText
     * @param  {Suggestion} suggestion
     * @return {String}
     */
    getDisplayText(suggestion) {
      if (suggestion) {
        return this._formatters[suggestion.type].getDisplayText(suggestion)
      }

      if (suggestion === null && this.noSelectionLabel) {
        return this.noSelectionLabel
      }

      return suggestion
    }

    /**
     * Get the template to use to render the given suggestion.
     *
     * @method getTemplate
     * @param  {Suggestion} suggestion
     *
     * @return {String|undefined} The template to use or `undefined` if the default template should be used
     */
    getTemplate(suggestion) {
      return suggestion && this._formatters[suggestion.type].template
    }

    /**
     * Whether or not this source applies to multiple types of data.
     *
     * @method isMultiType
     * @return {Boolean}
     */
    isMultiType() {
      return this.types.length > 1
    }

    /**
     * Whether or not this source is configured to group suggestions together.
     *
     * @method isGrouped
     * @return {Boolean}
     */
    isGrouped() {
      if (this.isMultiType()) {
        return false
      }

      const formatter = this._formatters[this._getTypeName(this.types[0])]
      return angular.isFunction(formatter.groupBy)
    }

    /**
     * Get the displayable version of the type with the given type name.
     *
     * @method getTypeDisplay
     * @param  {String} typeName Name of the type to display
     * @return {String}
     */
    getTypeDisplay(typeName) {
      const typeDefinition = this._getTypeDefinition(typeName)

      if (typeDefinition && typeDefinition.display) {
        return typeDefinition.display
      }

      return this._getTypeName(typeDefinition)
    }

    /**
     * Get the displayable plural version of the type with the given type name.
     *
     * @method getTypePluralDisplay
     * @param  {String} typeName Name of the type to display
     * @return {String}
     */
    getTypePluralDisplay(typeName) {
      const typeDefinition = this._getTypeDefinition(typeName)

      if (typeDefinition && typeDefinition.displayPlural) {
        return typeDefinition.displayPlural
      }

      const displayType = this.getTypeDisplay(typeName)
      return angular.isString(displayType) ? `${displayType}s` : displayType
    }

    /**
     * Whether or not the user is able to create a new item based on the source configuration (is there a `create`
     * function configured) and current query, suggestions and selected suggestions (to ensure the created item would
     * not be a duplicate).
     *
     * @method canCreate
     *
     * @param  {Object} options
     *         @param {String} options.query
     *                The current query string
     *         @param {Array<Suggestions>} options.suggestions
     *                Array of currently displayed suggestions
     *         @param {Suggestion|Array<Suggestions>} options.selected
     *                The already selected suggestion(s)
     *
     * @return {Boolean} true if the user can create a new item, false otherwise
     */
    canCreate({ query, suggestions, selected }) {
      const canShowCreate = this.canShowCreate ? this.canShowCreate() : true
      return Boolean(
        canShowCreate &&
          this.create &&
          query &&
          !this._isDuplicate({ query, suggestions, selected })
      )
    }

    /**
     * Whether or not the user is able to clear the currently selected suggestion(s) based on whether something is
     * selected, the current query and whether or not the user could create a new item.
     *
     * @method canClear
     *
     * @param  {Object} options
     *         @param {String} options.query
     *                The current query string
     *         @param {Array<Suggestions>} options.suggestions
     *                Array of currently displayed suggestions
     *         @param {Suggestion|Array<Suggestions>} options.selected
     *                The already selected suggestion(s)
     *
     * @return {Boolean} true if the user can clear the selected item, false otherwise
     */
    canClear({ query, suggestions, selected }) {
      const hasSelected = Boolean(
        angular.isArray(selected) ? selected.length : selected
      )
      return Boolean(
        hasSelected &&
          !query &&
          !this.canCreate({ query, suggestions, selected })
      )
    }

    // ============================== Private Methods =============================== //

    _doFeelingLucky({ query, pendingSuggestion, suggestions, selected }) {
      if (!this._feelingLucky) {
        return
      }

      const pendingCreate =
        !pendingSuggestion &&
        this.canCreate({ query, suggestions, selected }) &&
        this.allowFeelingLuckyCreate

      const suggestion = this._feelingLucky({
        query,
        pendingSuggestion,
        pendingCreate,
      })
      if (!suggestion) {
        if (pendingCreate) {
          return this.create({ query })
        }
        return
      }

      return $q.resolve(suggestion).then(resolvedSuggestion => {
        if (resolvedSuggestion) {
          return this._enhanceSuggestion(resolvedSuggestion)
        } else if (pendingCreate) {
          return this.create({ query })
        }
      })
    }

    _isDuplicate({ query, suggestions, selected }) {
      return some(suggestions.concat(selected), suggestion =>
        this._isSuggestionDuplicate({ query, suggestion })
      )
    }

    _isSuggestionDuplicate({ query, suggestion }) {
      if (!suggestion) {
        return false
      }
      return (
        lowerCaseTrim(query) === lowerCaseTrim(this.getDisplayText(suggestion))
      )
    }

    _getTypeDefinition(typeName) {
      if (!this.isMultiType() && !typeName) {
        return this.types[0]
      }

      const typeDefinition = find(this.types, typeDef => {
        return typeDef === typeName || typeDef.name === typeName
      })
      return typeDefinition || typeName
    }

    _getTypeName(type) {
      return type.name || type
    }

    _search(options) {
      return this.init()
        .then(() => {
          try {
            return this._doSearch(options)
          } catch (e) {
            return $q.reject(e)
          }
        })
        .then(suggestions => this._enhanceSuggestions(suggestions))
    }

    _searchSources(options) {
      return $q
        .all(map(this._sources, source => source._search(options)))
        .then(suggestions =>
          uniqBy(
            flatten(suggestions),
            suggestion => suggestion.id + suggestion.type
          )
        )
    }

    _hydrateRawSuggestion(entity) {
      if (!entity || !this._rawSuggestions) {
        return entity
      }

      const type = this._getTypeName(this.types[0])
      return {
        id: entity.id,
        type,
        [type]: entity,
      }
    }

    _enhanceSuggestions(suggestions) {
      const enhancedSuggestions = []
      return $q
        .all(
          map(suggestions, (suggestion, index) => {
            return $q
              .resolve(this._enhanceSuggestion(suggestion))
              .then(enhancedSuggestion => {
                enhancedSuggestions[index] = enhancedSuggestion
              })
              .catch(error => {
                $log.warn(
                  '[VdSuggestionSource] Error enhancing search suggestion',
                  error
                )
                // Swallow the error so we still display the list of all other suggestions
              })
          })
        )
        .then(() => compact(enhancedSuggestions))
    }

    _enhanceSuggestion(suggestion) {
      return $q((resolve, reject) => {
        const hydratedSuggestion = this._hydrateRawSuggestion(suggestion)

        if (!hydratedSuggestion) {
          return resolve(hydratedSuggestion)
        }

        try {
          const formatter = this._formatters[hydratedSuggestion.type]
          $q.resolve(formatter.enhance(hydratedSuggestion)).then(
            resolve,
            reject
          )
        } catch (e) {
          reject(e)
        }
      })
    }

    _insertNullSuggestion(data) {
      const { suggestions, query } = data

      if (this.noSelectionLabel) {
        const lowerCaseLabel = lowerCaseTrim(this.noSelectionLabel)
        if (!query || lowerCaseLabel.indexOf(lowerCaseTrim(query)) > -1) {
          suggestions.unshift(null)
        }
      }

      return data
    }

    _doCreate({ query }) {
      return $q
        .resolve(this._create({ query }))
        .then(suggestion => this._enhanceSuggestion(suggestion))
    }

    _getSortOrder() {
      const formatter = this._formatters[this._getTypeName(this.types[0])]
      const ordering = {}

      if (formatter.groupOrder) {
        // Create a map as an efficient lookup for the sort order.
        //
        // e.g. Given an ordering of ['Tall', 'Short', 'Medium']
        // Produce the map:
        // {
        //   tall: 0,
        //   short: 1,
        //   medium: 2
        // }
        formatter.groupOrder.forEach((groupName, index) => {
          ordering[groupName.toLowerCase()] = index
        })
      }

      return ordering
    }

    _groupBy(data) {
      const { suggestions } = data

      if (!suggestions.length || !this.isGrouped()) {
        data.groups = [
          {
            startIndex: 0,
            suggestions,
          },
        ]
        return data
      }

      const formatter = this._formatters[this._getTypeName(this.types[0])]

      const grouped = groupBy(suggestions, suggestion => {
        if (suggestion === null) {
          return NULL_LABEL_GROUP
        }
        return formatter.groupBy(suggestion)
      })

      const groups = map(grouped, (suggestions, groupName) => {
        const name =
          isEmpty(groupName) || groupName === NULL_LABEL_GROUP
            ? null
            : groupName
        return { name, suggestions }
      })

      groups.sort(this._sortGroups(this._getSortOrder()))

      data.suggestions = []
      reduce(
        groups,
        (startIndex, group) => {
          group.startIndex = startIndex
          data.suggestions = data.suggestions.concat(group.suggestions)
          return startIndex + group.suggestions.length
        },
        0
      )

      data.groups = groups

      return data
    }

    _sortGroups(sortOrder) {
      return (groupA, groupB) => {
        if (groupA.name === null) {
          return -1
        }
        if (groupB.name === null) {
          return 1
        }

        const nameA = groupA.name.toLowerCase()
        const nameB = groupB.name.toLowerCase()

        // If both groups have a specific sort order, compare their value in the
        // sortOrder map.
        if (nameA in sortOrder && nameB in sortOrder) {
          return sortOrder[nameA] - sortOrder[nameB]
        }

        // If group B has a specific sort order and A doesn't, B should come first.
        if (!(nameA in sortOrder) && nameB in sortOrder) {
          return 1
        }

        // If group A has a specific sort order and B doesn't, A should come first.
        if (nameA in sortOrder && !(nameB in sortOrder)) {
          return -1
        }

        // If neither names have a sort order, sort alphabetically.
        if (nameA < nameB) {
          return -1
        }

        if (nameB < nameA) {
          return 1
        }

        return 0
      }
    }
  }

  return VdSuggestionSource
}
